import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Mail, Lock, ShieldAlert, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/supabase/AuthContext";

const Login: React.FC = () => {
  const { status, signIn, missing } = useAuth();
  const navigate = useNavigate();
  const location = useLocation() as any;
  const redirectTo = location.state?.from?.pathname || "/dashboard";

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const notConfigured = status === "not_configured";

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (notConfigured || submitting) return;
    setSubmitting(true);
    setError(null);
    const result = await signIn(email.trim(), password);
    setSubmitting(false);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    navigate(redirectTo, { replace: true });
  }

  return (
    <div
      data-testid="login-page"
      className="relative min-h-screen bg-background text-foreground"
    >
      <div className="texture-grain pointer-events-none absolute inset-0" />
      <div className="relative mx-auto flex min-h-screen max-w-6xl flex-col px-6 py-8 md:py-12">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary">
            <span className="font-serif text-xl text-primary-foreground">K</span>
          </div>
          <div className="leading-tight">
            <div className="font-serif text-base text-foreground">
              Kartalyuvası
            </div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              IFM Analiz
            </div>
          </div>
        </div>

        <div className="grid flex-1 items-center gap-10 py-10 md:grid-cols-2 md:py-16 md:gap-16">
          {/* Sol: Editöryel tanıtım */}
          <div className="space-y-6">
            <span className="eyebrow">Dahili analiz paneli</span>
            <h1 className="font-serif text-5xl leading-[1.03] text-foreground md:text-6xl">
              Satış, stok ve cari{" "}
              <em className="not-italic text-accent">tek bir yerde.</em>
            </h1>
            <p className="max-w-md text-base text-muted-foreground sm:text-lg">
              Nebim satış ve stok dosyalarınızı, Beşiktaş tedarikçi faturalarınızı
              güvenli şekilde içe aktarın; raporlar ve yapay zekâ yorumları ile
              birlikte değerlendirin.
            </p>
            <ul className="space-y-2 text-sm text-foreground/80">
              <li className="flex items-start gap-2">
                <span className="mt-1.5 inline-flex h-1.5 w-1.5 rounded-full bg-accent" />
                Deterministik finansal hesaplar — KDV ve kâr kod ile hesaplanır.
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 inline-flex h-1.5 w-1.5 rounded-full bg-accent" />
                Supabase RLS ile şirketler arası veri izolasyonu.
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 inline-flex h-1.5 w-1.5 rounded-full bg-accent" />
                Cloudflare Workers AI analiz ve ürün eşleştirme önerileri sunar; finansal hesapları uygulamanın doğrulanmış verileri yapar.
              </li>
            </ul>
          </div>

          {/* Sağ: Form kartı */}
          <div className="rounded-xl border border-border bg-card p-6 shadow-sm md:p-8">
            <div className="mb-6 space-y-1.5">
              <h2 className="font-serif text-2xl text-foreground">Giriş yap</h2>
              <p className="text-sm text-muted-foreground">
                E-posta ve parolanızla devam edin.
              </p>
            </div>

            {notConfigured ? (
              <div className="mb-5 flex items-start gap-3 rounded-md border border-accent/30 bg-accent/5 px-3.5 py-3">
                <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                <div className="space-y-1 text-xs leading-relaxed">
                  <p className="font-semibold text-foreground">
                    Supabase bağlantısı yapılandırılmadı
                  </p>
                  <p className="text-foreground/80">
                    Ortam değişkenleri girilince giriş formu otomatik olarak
                    aktifleşir.
                  </p>
                  {missing.length ? (
                    <ul className="mt-1 font-mono text-[11px] text-muted-foreground">
                      {missing.map((m) => (
                        <li key={m}>• {m}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </div>
            ) : null}

            {error ? (
              <div
                role="alert"
                data-testid="login-error"
                className="mb-5 flex items-start gap-3 rounded-md border border-destructive/40 bg-destructive/5 px-3.5 py-3"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                <p className="text-xs leading-relaxed text-foreground">
                  {error}
                </p>
              </div>
            ) : null}

            <form
              className="space-y-4"
              onSubmit={onSubmit}
              data-testid="login-form"
            >
              <div className="space-y-2">
                <Label htmlFor="email">E-posta</Label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="ad.soyad@kartalyuvasi.com"
                    autoComplete="email"
                    required
                    disabled={notConfigured || submitting}
                    value={email}
                    onChange={(e: any) => setEmail(e.target.value)}
                    className="pl-9"
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Parola</Label>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                    disabled={notConfigured || submitting}
                    value={password}
                    onChange={(e: any) => setPassword(e.target.value)}
                    className="pl-9"
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={notConfigured || submitting || !email || !password}
                className="w-full"
                data-testid="login-submit-button"
              >
                {submitting ? "Giriş yapılıyor…" : "Giriş yap"}
              </Button>

              <p className="text-center text-xs text-muted-foreground">
                İlk kullanıcılar: İbrahim Özşeker & Orkun Oral — hesaplar
                Supabase üzerinde oluşturulduğunda giriş aktifleşir.
              </p>
            </form>
          </div>
        </div>

        <div className="border-t border-border pt-4 text-[11px] text-muted-foreground">
          © {new Date().getFullYear()} Kartalyuvası • Dahili kullanım
        </div>
      </div>
    </div>
  );
};

export default Login;
